import * as React from 'react';
import { useState } from 'react';
import dateUtil from '../../../utils/dateUtil';
import { Employee } from '../../../hooks/useEmployeeData';
import ChangeRequestService, { ChangeRequest } from '../services/ChangeRequestService';
import TimeTrackingService from '../services/TimeTrackingService';
import WorkAreaService from '../../workareas/services/WorkAreaService';
import { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import { toast } from 'react-toastify';
import { DateRange } from './DateRangeFilter';
import { calcBreakMinutesNumber } from './useTimeTrackings';

interface WorkArea {
  id: number;
  name: string;
  [key: string]: any;
}

interface BreakPeriod {
  id?: number;
  start_time: string;
  end_time: string;
  [key: string]: any;
}

interface TimeTracking {
  id: number;
  employee: number;
  work_area: number;
  work_area__name?: string;
  start_time: string;
  end_time: string;
  break_start?: string | null;
  break_end?: string | null;
  breakperiods?: BreakPeriod[];
  [key: string]: any;
}

interface TTChangeRequestModalProps {
  visible: boolean;
  onClose: () => void;
  crExisting: ChangeRequest | null;
  setCrExisting: React.Dispatch<React.SetStateAction<ChangeRequest | null>>;
  crViewLoading: boolean;
  crViewError: string;
  crEditMode: boolean;
  setCrEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  crEditError: string;
  setCrEditError: React.Dispatch<React.SetStateAction<string>>;
  crConflicts: any[];
  setCrConflicts: React.Dispatch<React.SetStateAction<any[]>>;
  crModalMode: 'normal' | 'fallback';
  crSourceTT: TimeTracking | null;
  crEditStartDate: string;
  setCrEditStartDate: React.Dispatch<React.SetStateAction<string>>;
  crEditEndDate: string;
  setCrEditEndDate: React.Dispatch<React.SetStateAction<string>>;
  crEditStartTime: string;
  setCrEditStartTime: React.Dispatch<React.SetStateAction<string>>;
  crEditEndTime: string;
  setCrEditEndTime: React.Dispatch<React.SetStateAction<string>>;
  crEditBreakMinutes: string;
  setCrEditBreakMinutes: React.Dispatch<React.SetStateAction<string>>;
  crEditNotes: string;
  setCrEditNotes: React.Dispatch<React.SetStateAction<string>>;
  crWorkAreaId: number | null;
  setCrWorkAreaId: React.Dispatch<React.SetStateAction<number | null>>;
  employees: Employee[];
  workAreas: WorkArea[];
  setApprovedCrMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  setPendingCrMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  setRestoredOriginalMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  markCrPresence: (ttId: number, has: boolean) => void;
  setTimeTrackings: React.Dispatch<React.SetStateAction<TimeTracking[]>>;
  dateRange: DateRange | null;
  selectedWAs: number[];
  activeEmployeeId: number | null;
  loadTimeTrackings: (range: DateRange, workAreaIds: number[], employeeId?: number | null) => Promise<void>;
  handleReverseClick: (ttId: number) => Promise<void>;
  toUTCIfNaiveLocal: (iso?: string | null) => string | undefined;
  normalizeUTC: (iso?: string | null) => string;
  extractTimeFromISO: (iso: any) => string;
}

export default function TTChangeRequestModal({
  visible,
  onClose,
  crExisting,
  setCrExisting,
  crViewLoading,
  crViewError,
  crEditMode,
  setCrEditMode,
  crEditError,
  setCrEditError,
  crConflicts,
  setCrConflicts,
  crModalMode,
  crSourceTT,
  crEditStartDate,
  setCrEditStartDate,
  crEditEndDate,
  setCrEditEndDate,
  crEditStartTime,
  setCrEditStartTime,
  crEditEndTime,
  setCrEditEndTime,
  crEditBreakMinutes,
  setCrEditBreakMinutes,
  crEditNotes,
  setCrEditNotes,
  crWorkAreaId,
  setCrWorkAreaId,
  employees,
  workAreas,
  setApprovedCrMap,
  setPendingCrMap,
  setRestoredOriginalMap,
  markCrPresence,
  setTimeTrackings,
  dateRange,
  selectedWAs,
  activeEmployeeId,
  loadTimeTrackings,
  handleReverseClick,
  toUTCIfNaiveLocal,
  normalizeUTC,
  extractTimeFromISO,
}: TTChangeRequestModalProps): React.JSX.Element | null {
  if (!visible) return null;

  const [crAllWAs, setCrAllWAs] = useState<WorkArea[]>([]);
  const [crShowWA, setCrShowWA] = useState<boolean>(false);
  const [crLoadingWA, setCrLoadingWA] = useState<boolean>(false);

  const btnStyle: React.CSSProperties = {
    ...ps.buttonPrimary,
    padding:'6px 12px',
    fontSize:'11px',
  };

  const btnSecondaryStyle: React.CSSProperties = {
    ...ps.buttonSecondary,
    padding: '6px 12px',
    fontSize: '11px',
  };

  const handlePrimaryHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.transform = 'translateY(-1px)';
    e.currentTarget.style.boxShadow = shadows.medium;
  };
  const handlePrimaryLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = shadows.card;
  };
  const handleSecondaryHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = colors.bgHover;
    e.currentTarget.style.borderColor = colors.textMuted;
  };
  const handleSecondaryLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = colors.bgCard;
    e.currentTarget.style.borderColor = colors.border;
  };

  const fmtLocalFancy = (iso?: string | null): string => {
    if (!iso) return '';
    const normalized = toUTCIfNaiveLocal(iso) || normalizeUTC(iso);
    const dt = dateUtil.convertUTCToTenantTime(normalized);
    if (!dt) return '';
    const wd = dt.toLocaleDateString('en-GB', { weekday:'short' });
    const dd = String(dt.getDate()).padStart(2,'0');
    const mm = String(dt.getMonth()+1).padStart(2,'0');
    const yy = String(dt.getFullYear()).slice(-2);
    const hh = String(dt.getHours()).padStart(2,'0');
    const mi = String(dt.getMinutes()).padStart(2,'0');
    return `${wd}, ${dd}.${mm}.${yy}, ${hh}:${mi} h`;
  };

  const pickFirst = (obj: any, keys: string[]): any => {
    if (!obj) return undefined;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return undefined;
  };

  const getOriginalStart = (cr?: any) => {
    const direct = pickFirst(cr || {}, ['original_start_time', 'original_start_datetime', 'original_start']);
    if (direct !== undefined) return direct;
    return (cr && cr.original && cr.original.start_time) ? cr.original.start_time : undefined;
  };
  const getOriginalEnd = (cr?: any) => {
    const direct = pickFirst(cr || {}, ['original_end_time', 'original_end_datetime', 'original_end']);
    if (direct !== undefined) return direct;
    return (cr && cr.original && cr.original.end_time) ? cr.original.end_time : undefined;
  };
  const getRequestedStart = (cr?: any) => pickFirst(cr || {}, ['requested_start_time', 'proposed_start_time', 'requested_start_datetime', 'requested_start']);
  const getRequestedEnd = (cr?: any) => pickFirst(cr || {}, ['requested_end_time', 'proposed_end_time', 'requested_end_datetime', 'requested_end']);
  const getOriginalWAName = (cr?: any) => pickFirst(cr || {}, ['original_work_area__name', 'original_work_area_name']);

  const computeNetFromRange = (startIso?: string | null, endIso?: string | null, breakMinutes?: number | null): string => {
    if (!startIso || !endIso) return '';
    const st = new Date(startIso);
    const et = new Date(endIso);
    const ms = Math.max(0, et.getTime() - st.getTime() - (breakMinutes || 0) * 60000);
    const h = Math.round((ms / 3600000) * 10) / 10;
    return `${h.toFixed(1)} h`;
  };

  const computeOverrideTimes = (): { override_start_time?: string | null; override_end_time?: string | null } => {
    const startIso = (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || crSourceTT?.start_time || null;
    const endIso = (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || crSourceTT?.end_time || null;
    const normalizeForApi = (iso?: string | null): string | null | undefined => {
      if (!iso) return undefined;
      const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
      if (hasTz) return iso;
      const d = dateUtil.extractDateFromISO(iso);
      const t = dateUtil.extractTimeFromISO(iso);
      if (d && t) return dateUtil.createTenantTimeAsUTC(d, t);
      return iso;
    };
    return {
      override_start_time: normalizeForApi(startIso),
      override_end_time: normalizeForApi(endIso)
    };
  };

  const buildISOFromFields = (dateStr?: string, timeStr?: string): string | null => {
    if (!dateStr || !timeStr) return null;
    return dateUtil.createTenantTimeAsUTC(dateStr, timeStr);
  };

  const handleOpenWASection = async () => {
    setCrShowWA(prev => !prev);
    if (!crShowWA) {
      setCrLoadingWA(true);
      try {
        const all = await WorkAreaService.fetchWorkAreas();
        setCrAllWAs(all);
      } catch (error) {
        console.error('CR WA load =>', error);
        if (error instanceof HttpError) {
          globalErrorHandler.handle(error);
        }
      } finally {
        setCrLoadingWA(false);
      }
    }
  };

  const submitCRViewEdit = async () => {
    if (!crExisting) return;
    const startIso = buildISOFromFields(crEditStartDate, crEditStartTime);
    const endIso = buildISOFromFields(crEditEndDate, crEditEndTime);

    let notes: string | null = null;
    if (crWorkAreaId && crExisting.original_work_area && crWorkAreaId !== crExisting.original_work_area) {
      const waObj = crAllWAs.find(w => w.id === crWorkAreaId);
      if (waObj) {
        notes = `WA requested: ${waObj.name} (${waObj.id})`;
      } else {
        notes = `WA requested: (${crWorkAreaId})`;
      }
    }
    const finalNotes = [crEditNotes || '', notes || ''].filter(Boolean).join('\n');

    const breakTrimmed = crEditBreakMinutes.trim();
    const breakMinutes = breakTrimmed === '' ? null : Number(breakTrimmed);
    if (breakMinutes !== null && (!Number.isInteger(breakMinutes) || breakMinutes < 0)) {
      setCrEditError('Pause muss eine ganze Zahl ≥ 0 (Minuten) sein.');
      return;
    }
    const initialBreak = crExisting.proposed_break_duration
      ?? crExisting.original_break_duration
      ?? (crSourceTT ? calcBreakMinutesNumber(crSourceTT as any) : null);
    const breakChanged = crExisting.proposed_break_duration != null || breakMinutes !== initialBreak;

    try {
      await ChangeRequestService.submitEmployeeReview(crExisting.id, {
        new_start_time: startIso,
        new_end_time: endIso,
        employee_notes: finalNotes || undefined,
        ...(breakChanged ? { proposed_break_duration: breakMinutes } : {})
      });
      setCrEditMode(false);
      setCrEditError('');
      onClose();
    } catch (error) {
      console.error('CR submit =>', error);
      if (error instanceof HttpError) {
        if (!globalErrorHandler.handle(error)) {
          toast.error('Failed to submit');
        }
      } else {
        toast.error('Failed to submit');
      }
    }
  };

  const closeCRViewModal = onClose;

  const handleApprove = async () => {
    if (!crExisting) return;
    try {
      const overrides = computeOverrideTimes();
      await ChangeRequestService.approve(crExisting.id, overrides);
      try {
        if (crSourceTT?.id) {
          const start = (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || overrides.override_start_time || '';
          const end = (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || overrides.override_end_time || '';
          const nStart = toUTCIfNaiveLocal(start);
          const nEnd = toUTCIfNaiveLocal(end);
          if (nStart && nEnd) setApprovedCrMap(prev => ({ ...prev, [crSourceTT.id]: { start: nStart, end: nEnd } }));
        }
      } catch {}
      if (crSourceTT?.id) {
        setPendingCrMap(prev => {
          const next = { ...prev } as any;
          delete next[crSourceTT.id];
          return next;
        });
      }
      markCrPresence(crSourceTT?.id || 0, false);
      closeCRViewModal();
      if (dateRange) {
        await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
      } else if (crSourceTT?.id) {
        try {
          const fresh = await TimeTrackingService.fetchTimeTracking(crSourceTT.id);
          setTimeTrackings(prev => prev.map(i => i.id === fresh.id ? { ...i, ...fresh } as any : i));
        } catch {}
      }
    } catch (e) {
      const detail = (e as any)?.response?.data?.detail || '';
      const conflicts = (e as any)?.response?.data?.conflicts || [];
      if (Array.isArray(conflicts) && conflicts.length > 0) {
        setCrConflicts(conflicts);
      }
      if (typeof detail === 'string' && detail.includes('Nur PENDING')) {
        try {
          await ChangeRequestService.submitEmployeeReview(crExisting.id, {
            new_start_time: (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || crSourceTT?.start_time || null,
            new_end_time: (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || crSourceTT?.end_time || null,
            employee_notes: (crExisting as any)?.employee_notes || undefined
          });
          const overrides2 = computeOverrideTimes();
          await ChangeRequestService.approve(crExisting.id, overrides2);
          try {
            if (crSourceTT?.id) {
              const start = (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || overrides2.override_start_time || '';
              const end = (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || overrides2.override_end_time || '';
              const nStart = toUTCIfNaiveLocal(start);
              const nEnd = toUTCIfNaiveLocal(end);
              if (nStart && nEnd) setApprovedCrMap(prev => ({ ...prev, [crSourceTT.id]: { start: nStart, end: nEnd } }));
            }
          } catch {}
          if (crSourceTT?.id) {
            setPendingCrMap(prev => {
              const next = { ...prev } as any;
              delete next[crSourceTT.id];
              return next;
            });
          }
          markCrPresence(crSourceTT?.id || 0, false);
          closeCRViewModal();
          if (dateRange) {
            await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
          } else if (crSourceTT?.id) {
            try {
              const fresh = await TimeTrackingService.fetchTimeTracking(crSourceTT.id);
              setTimeTrackings(prev => prev.map(i => i.id === fresh.id ? { ...i, ...fresh } as any : i));
            } catch {}
          }
        } catch (e2) {
          toast.error('Approve failed (pending transition)');
        }
      } else if (typeof detail === 'string' && detail.includes('Überlappung')) {
        setCrEditError('Konflikt mit anderen TTs vorhanden. Du kannst Auto-Resolve verwenden (kappen).');
      } else {
        try {
          const ov = computeOverrideTimes();
          await ChangeRequestService.submitEmployeeReview(crExisting.id, {
            new_start_time: (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || ov.override_start_time || null,
            new_end_time: (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || ov.override_end_time || null,
            employee_notes: (crExisting as any)?.employee_notes || undefined
          });
          await ChangeRequestService.approve(crExisting.id, ov);
          try {
            if (crSourceTT?.id) {
              const start = (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || ov.override_start_time || '';
              const end = (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || ov.override_end_time || '';
              const nStart = toUTCIfNaiveLocal(start);
              const nEnd = toUTCIfNaiveLocal(end);
              if (nStart && nEnd) setApprovedCrMap(prev => ({ ...prev, [crSourceTT.id]: { start: nStart, end: nEnd } }));
            }
          } catch {}
          if (crSourceTT?.id) {
            setPendingCrMap(prev => {
              const next = { ...prev } as any;
              delete next[crSourceTT.id];
              return next;
            });
          }
          markCrPresence(crSourceTT?.id || 0, false);
          closeCRViewModal();
          if (dateRange) {
            await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
          } else if (crSourceTT?.id) {
            try {
              const fresh = await TimeTrackingService.fetchTimeTracking(crSourceTT.id);
              setTimeTrackings(prev => prev.map(i => i.id === fresh.id ? { ...i, ...fresh } as any : i));
            } catch {}
          }
        } catch (e4) {
          const d = (e as any)?.response?.data?.detail;
          toast.error((typeof d === 'string' && d) ? d : 'Approve failed');
        }
      }
    }
  };

  const handleAutoResolve = async () => {
    if (!crExisting) return;
    try {
      const overrides = computeOverrideTimes();
      await ChangeRequestService.approve(crExisting.id, { resolve_conflicts: 'truncate', ...overrides });
      try {
        if (crSourceTT?.id) {
          const start = (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || overrides.override_start_time || '';
          const end = (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || overrides.override_end_time || '';
          const nStart = toUTCIfNaiveLocal(start);
          const nEnd = toUTCIfNaiveLocal(end);
          if (nStart && nEnd) setApprovedCrMap(prev => ({ ...prev, [crSourceTT.id]: { start: nStart, end: nEnd } }));
        }
      } catch {}
      if (crSourceTT?.id) {
        setPendingCrMap(prev => {
          const next = { ...prev } as any;
          delete next[crSourceTT.id];
          return next;
        });
      }
      markCrPresence(crSourceTT?.id || 0, false);
      closeCRViewModal();
      if (dateRange) {
        await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
      } else if (crSourceTT?.id) {
        try {
          const fresh = await TimeTrackingService.fetchTimeTracking(crSourceTT.id);
          setTimeTrackings(prev => prev.map(i => i.id === fresh.id ? { ...i, ...fresh } as any : i));
        } catch {}
      }
    } catch (e3) {
      const conflicts2 = (e3 as any)?.response?.data?.conflicts || [];
      if (Array.isArray(conflicts2) && conflicts2.length > 0) setCrConflicts(conflicts2);
      try {
        const ov = computeOverrideTimes();
        await ChangeRequestService.submitEmployeeReview(crExisting.id, {
          new_start_time: (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || ov.override_start_time || null,
          new_end_time: (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || ov.override_end_time || null,
          employee_notes: (crExisting as any)?.employee_notes || undefined
        });
        await ChangeRequestService.approve(crExisting.id, { resolve_conflicts: 'truncate', ...ov });
        try {
          if (crSourceTT?.id) {
            const start = (crExisting as any)?.new_start_time || getRequestedStart(crExisting) || ov.override_start_time || '';
            const end = (crExisting as any)?.new_end_time || getRequestedEnd(crExisting) || ov.override_end_time || '';
            const nStart = toUTCIfNaiveLocal(start);
            const nEnd = toUTCIfNaiveLocal(end);
            if (nStart && nEnd) setApprovedCrMap(prev => ({ ...prev, [crSourceTT.id]: { start: nStart, end: nEnd } }));
          }
        } catch {}
        if (crSourceTT?.id) {
          setPendingCrMap(prev => {
            const next = { ...prev } as any;
            delete next[crSourceTT.id];
            return next;
          });
        }
        markCrPresence(crSourceTT?.id || 0, false);
        closeCRViewModal();
        if (dateRange) {
          await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
        } else if (crSourceTT?.id) {
          try {
            const fresh = await TimeTrackingService.fetchTimeTracking(crSourceTT.id);
            setTimeTrackings(prev => prev.map(i => i.id === fresh.id ? { ...i, ...fresh } as any : i));
          } catch {}
        }
      } catch {
        toast.error('Auto-Resolve failed');
      }
    }
  };

  const handleDelete = async () => {
    if (!crExisting) return;
    try {
      const ok = window.confirm('Möchtest du diesen Change Request wirklich löschen?');
      if (!ok) return;
      await ChangeRequestService.deleteChangeRequest(crExisting.id);
      if (crSourceTT?.id) {
        setApprovedCrMap(prev => {
          const next = { ...prev } as any;
          delete next[crSourceTT.id];
          return next;
        });
        setPendingCrMap(prev => {
          const next = { ...prev } as any;
          delete next[crSourceTT.id];
          return next;
        });
        const startRaw = getOriginalStart(crExisting) || crSourceTT.start_time;
        const endRaw = getOriginalEnd(crExisting) || crSourceTT.end_time;
        const start = toUTCIfNaiveLocal(startRaw);
        const end = toUTCIfNaiveLocal(endRaw);
        if (start && end) {
          setRestoredOriginalMap(prev => ({ ...prev, [crSourceTT.id!]: { start, end } }));
        } else {
          setRestoredOriginalMap(prev => {
            const next = { ...prev } as any;
            delete next[crSourceTT.id!];
            return next;
          });
        }
        markCrPresence(crSourceTT.id, false);
      }
      closeCRViewModal();
      if (dateRange) {
        await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
      }
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div
      style={{
        position:'fixed', top:0,left:0,right:0,bottom:0,
        backgroundColor:'rgba(0,0,0,0.4)',
        display:'flex', alignItems:'center', justifyContent:'center',
        zIndex: 2500
      }}
      onClick={closeCRViewModal}
    >
      <div style={{ width:'520px', background: colors.bgCard, padding:'20px', borderRadius:'6px', position:'relative', boxShadow: shadows.elevated }} onClick={(e)=>e.stopPropagation()}>
        <button aria-label="Close" onClick={closeCRViewModal} style={{ position:'absolute', top:8, right:8, border:'none', background:'transparent', fontSize:16, cursor:'pointer' }}>×</button>
        <h3 style={{ margin:'0 0 12px', fontSize:'1rem' }}>
          {crEditMode ? 'Edit Change Request' : (
            crModalMode === 'fallback'
              ? 'Time tracking corrected (Fallback)'
              : ((crExisting as any)?.status === 'APPROVED' ? 'Time tracking change approved' : 'Time Tracking Change requested')
          )}
        </h3>
        {crViewLoading && <p>Loading...</p>}
        {crViewError && !crExisting && <p style={{ color: colors.danger }}>{crViewError}</p>}
        {!crViewLoading && !crEditMode && (
          <div>
            {(crExisting as any)?.status === 'APPROVED' ? (
              <div style={{ marginBottom:'12px', color: colors.textBody }}>
                <div style={{ marginBottom:6 }}>Time tracking change approved by (Admin) on {fmtLocalFancy((crExisting as any)?.updated_at || (crExisting as any)?.resolved_at || (crExisting as any)?.created_at)}</div>
              </div>
            ) : (
              <div style={{ marginBottom:'12px', color: colors.textBody }}>
                <div style={{ marginBottom:6 }}>Time Tracking Change requested on {fmtLocalFancy((crExisting as any)?.created_at || (crExisting as any)?.updated_at)}</div>
              </div>
            )}
            <div style={{ marginBottom:'10px' }}>
              <div style={{ fontWeight:600, marginBottom:4 }}>Original</div>
              <div>
                {fmtLocalFancy(getOriginalStart(crExisting) || crSourceTT?.start_time)} – {fmtLocalFancy(getOriginalEnd(crExisting) || crSourceTT?.end_time)}
              </div>
              {(getOriginalWAName(crExisting) || crSourceTT?.work_area__name) && (
                <div style={{ color: colors.textMuted, marginTop:2 }}>Work Area: {getOriginalWAName(crExisting) || crSourceTT?.work_area__name}</div>
              )}
              <div style={{ color: colors.textLight, marginTop:2 }}>net {computeNetFromRange(getOriginalStart(crExisting) || crSourceTT?.start_time, getOriginalEnd(crExisting) || crSourceTT?.end_time)}</div>
            </div>
            {(crExisting as any)?.status !== 'APPROVED' && crConflicts.length > 0 && (
              <div style={{ background: colors.warningLight, border: `1px solid ${colors.warning}`, padding:'8px', borderRadius:'4px', marginBottom:'12px' }}>
                <div style={{ fontWeight:600, marginBottom:6 }}>Konflikte</div>
                <ul style={{ margin:0, paddingLeft:'18px' }}>
                  {crConflicts.map((c, idx) => (
                    <li key={idx}>
                      TT #{c.id || c.time_tracking_id || '?'}: {fmtLocalFancy(c.start_time || c.start || c.start_datetime)} – {fmtLocalFancy(c.end_time || c.end || c.end_datetime)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ marginBottom:'14px' }}>
              <div style={{ fontWeight:600, marginBottom:4 }}>{(crExisting as any)?.status === 'APPROVED' ? 'Corrected' : 'Correct time'}</div>
              <div>
                {fmtLocalFancy((crExisting as any)?.new_start_time || getRequestedStart(crExisting))} – {fmtLocalFancy((crExisting as any)?.new_end_time || getRequestedEnd(crExisting))}
              </div>
              {crExisting?.proposed_break_duration != null && (
                <div style={{ color: colors.textMuted, marginTop:2 }}>Pause: {crExisting.proposed_break_duration} min</div>
              )}
              <div style={{ color: colors.textLight, marginTop:2 }}>net {computeNetFromRange((crExisting as any)?.new_start_time || getRequestedStart(crExisting), (crExisting as any)?.new_end_time || getRequestedEnd(crExisting), crExisting?.proposed_break_duration)}</div>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              {!!crExisting && (crExisting as any)?.status !== 'APPROVED' && (
                <>
                  <button
                    style={{
                      ...btnStyle,
                      background: `linear-gradient(135deg, ${colors.success} 0%, #15803d 100%)`,
                      boxShadow: `0 2px 6px rgba(34, 197, 94, 0.3)`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = `0 4px 12px rgba(34, 197, 94, 0.4)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 2px 6px rgba(34, 197, 94, 0.3)`;
                    }}
                    onClick={handleApprove}
                  >Approve</button>
                  {crConflicts.length > 0 && (
                    <button
                      style={{
                        ...btnStyle,
                        background: `linear-gradient(135deg, ${colors.warning} 0%, #d97706 100%)`,
                        boxShadow: `0 2px 6px rgba(245, 158, 11, 0.3)`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = `0 4px 12px rgba(245, 158, 11, 0.4)`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = `0 2px 6px rgba(245, 158, 11, 0.3)`;
                      }}
                      onClick={handleAutoResolve}
                    >Auto-Resolve (kappen)</button>
                  )}
                  <button
                    style={{
                      ...btnStyle,
                      background: `linear-gradient(135deg, ${colors.danger} 0%, #b91c1c 100%)`,
                      boxShadow: `0 2px 6px rgba(239, 68, 68, 0.3)`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = `0 4px 12px rgba(239, 68, 68, 0.4)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 2px 6px rgba(239, 68, 68, 0.3)`;
                    }}
                    onClick={handleDelete}
                  >Delete</button>
                </>
              )}
              {(crExisting as any)?.status !== 'APPROVED' ? (
                <button style={btnStyle} onMouseEnter={handlePrimaryHover} onMouseLeave={handlePrimaryLeave} onClick={() => { setCrEditError(''); setCrEditMode(true); }}>Change</button>
              ) : null}
              {crModalMode === 'fallback' && crSourceTT?.id && (
                <button
                  style={btnSecondaryStyle}
                  onMouseEnter={handleSecondaryHover}
                  onMouseLeave={handleSecondaryLeave}
                  onClick={async () => {
                    await handleReverseClick(crSourceTT.id);
                    onClose();
                  }}
                  title="Reverse (Fallback)"
                >
                  Reverse
                </button>
              )}
              <button style={btnSecondaryStyle} onMouseEnter={handleSecondaryHover} onMouseLeave={handleSecondaryLeave} onClick={closeCRViewModal}>Close</button>
            </div>
          </div>
        )}

        {!crViewLoading && crExisting && crEditMode && (
          <div>
            {crEditError && (
              <div style={{ background: colors.dangerLight, color: colors.danger, padding:'8px', borderRadius:'4px', marginBottom:'10px' }}>
                {crEditError}
              </div>
            )}
            <div style={{ fontWeight:600, marginBottom:6 }}>Original</div>
            <div style={{ color: colors.textBody, marginBottom:10 }}>
              {fmtLocalFancy(crExisting.original_start_time)} – {fmtLocalFancy(crExisting.original_end_time)}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
              <div>
                <label style={{ display:'block', fontSize:'0.85rem', marginBottom:4 }}>Start Date</label>
                <input type="date" value={crEditStartDate} onChange={(e) => setCrEditStartDate(e.target.value)} style={{ width:'100%', padding:'6px' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'0.85rem', marginBottom:4 }}>End Date</label>
                <input type="date" value={crEditEndDate} onChange={(e) => setCrEditEndDate(e.target.value)} style={{ width:'100%', padding:'6px' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'0.85rem', marginBottom:4 }}>Start Time (HH:MM)</label>
                <input type="time" value={crEditStartTime} onChange={(e) => setCrEditStartTime(e.target.value)} style={{ width:'100%', padding:'6px' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'0.85rem', marginBottom:4 }}>End Time (HH:MM)</label>
                <input type="time" value={crEditEndTime} onChange={(e) => setCrEditEndTime(e.target.value)} style={{ width:'100%', padding:'6px' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'0.85rem', marginBottom:4 }}>Pause (Minuten)</label>
                <input type="number" min={0} step={1} value={crEditBreakMinutes} onChange={(e) => setCrEditBreakMinutes(e.target.value)} placeholder="0" style={{ width:'100%', padding:'6px' }} />
              </div>
            </div>

            <div style={{ marginBottom:'10px' }}>
              <button style={btnSecondaryStyle} onMouseEnter={handleSecondaryHover} onMouseLeave={handleSecondaryLeave} onClick={handleOpenWASection}>
                {crShowWA ? 'Hide Work Area' : 'Change Work Area'}
              </button>
              {crShowWA && (
                <div style={{ marginTop:'8px' }}>
                  {crLoadingWA ? (
                    <div>Loading work areas...</div>
                  ) : (
                    <select value={crWorkAreaId ?? ''} onChange={(e) => setCrWorkAreaId(e.target.value ? Number(e.target.value) : null)} style={{ padding:'6px', width:'100%' }}>
                      <option value="">(none)</option>
                      {crAllWAs.map(wa => (
                        <option key={wa.id} value={wa.id}>{wa.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>

            <div style={{ textAlign:'right' }}>
              <button style={btnStyle} onMouseEnter={handlePrimaryHover} onMouseLeave={handlePrimaryLeave} onClick={submitCRViewEdit}>Save</button>
              <button style={{ ...btnSecondaryStyle, marginLeft:8 }} onMouseEnter={handleSecondaryHover} onMouseLeave={handleSecondaryLeave} onClick={() => setCrEditMode(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
